import React, { useEffect, useState } from 'react';
import { collection, getDocs, Timestamp, query, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from "../firebase/firebaseConfig";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface AttributionData {
  createdAt: Timestamp;
  deviceId: string;
  firstOptionId: string;
  firstOptionTitle: string;
  hashedDeviceId: string;
  secondOptionId: string;
  secondOptionTitle: string;
}

interface AggregatedData {
  [key: string]: number;
}

interface AggregatedResults {
  byFirstOption: AggregatedData;
  byBothOptions: AggregatedData;
  bySecondOption: AggregatedData;
}

interface DailyData {
  [date: string]: {
    [option: string]: number;
  };
}

// CSS 스타일을 컴포넌트 상단에 추가
const styles = `
  .attribution-container {
    padding: 32px;
    background-color: #f8f9fa;
  }

  .page-title {
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 32px;
    color: #333;
  }

  .date-picker-section {
    background: white;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    margin-bottom: 32px;
  }

  .date-picker-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #444;
  }

  .date-picker-container {
    display: flex;
    gap: 24px;
  }

  .date-picker-wrapper {
    display: flex;
    flex-direction: column;
  }

  .date-picker-label {
    margin-bottom: 8px;
    font-size: 14px;
    color: #666;
  }

  .date-picker-input {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .table-section {
    background: white;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    margin-bottom: 32px;
  }

  .table-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #444;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
  }

  .data-table th {
    background-color: #f8f9fa;
    padding: 12px 16px;
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    border-bottom: 2px solid #eee;
  }

  .data-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    font-size: 14px;
    color: #444;
  }

  .data-table tr:hover {
    background-color: #f8f9fa;
  }

  .text-right {
    text-align: right;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .quick-select-buttons {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  .date-range-button {
    padding: 8px 16px;
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    color: #444;
    transition: all 0.2s;
  }

  .date-range-button:hover {
    background-color: #e0e0e0;
  }

  .date-range-button:active {
    background-color: #d0d0d0;
  }

  .data-table th.text-right,
  .data-table td.text-right {
    text-align: right;
    padding-right: 24px;  /* 오른쪽 패딩 추가 */
    width: 100px;         /* 셀 너비 고정 */
  }

  .data-table th:first-child,
  .data-table td:first-child {
    text-align: left;
    padding-left: 24px;   /* 왼쪽 패딩 추가 */
    min-width: 200px;     /* 첫 번째 열 최소 너비 설정 */
  }
`;

const AttributionPage: React.FC = () => {
  const [attributionData, setAttributionData] = useState<AttributionData[]>([]);
  const [dailyResults, setDailyResults] = useState<{
    byFirstOption: DailyData;
    byBothOptions: DailyData;
    bySecondOption: DailyData;
  }>({
    byFirstOption: {},
    byBothOptions: {},
    bySecondOption: {}
  });

  useEffect(() => {
    fetchAttributionData();
  }, []);

  const fetchAttributionData = async () => {
    try {
      const acquisitionRef = collection(db, 'UserAcquisition');
      console.log('Firestore 쿼리 시작');
      
      // 디버깅을 위한 로그 추가
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      
      let q = query(
        acquisitionRef,
        orderBy('createdAt', 'desc'),
        // 최근 7일 데이터만 가져오도록 수정
        // startAfter(sevenDaysAgo),
        limit(1000)
      );

      const snapshot = await getDocs(q);
      console.log('가져온 데이터 수:', snapshot.size);
      
      const allData: AttributionData[] = [];
      snapshot.forEach((doc) => {
        const docData = doc.data() as AttributionData;
        // 디버깅을 위한 로그 추가
        console.log('문서 데이터:', {
          createdAt: docData.createdAt.toDate(),
          firstOption: docData.firstOptionTitle,
          secondOption: docData.secondOptionTitle
        });
        allData.push(docData);
      });
      
      console.log('총 가져온 데이터 수:', allData.length);
      setAttributionData(allData);
      aggregateData(allData);
      
    } catch (error) {
      console.error('데이터 불러오기 실패:', error);
    }
  };

  const getDates = () => {
    const dates: string[] = [];
    
    // 현재 한국 시간 구하기
    const now = new Date();
    const kstOffset = 9 * 60; // 한국 시간 오프셋 (분)
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000); // 현재 UTC
    const kstNow = new Date(utc + (kstOffset * 60000)); // 한국 시간으로 변환
    
    // 시간을 당일 09:00:00으로 설정 (한국 시간 기준)
    kstNow.setHours(9, 0, 0, 0);
    
    console.log('기준 시작일:', kstNow.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }));
    
    // 최근 8일치 데이터 날짜 생성
    for (let i = 8; i >= 1; i--) {
        const date = new Date(kstNow);
        date.setDate(date.getDate() - (i-1));
        const dateStr = date.toISOString().split('T')[0];
        dates.push(dateStr);
        console.log(`날짜 추가: ${dateStr} (${date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })})`);
    }
    
    return dates;
  };

  const aggregateData = (data: AttributionData[]) => {
    const dates = getDates();
    const results = {
        byFirstOption: {} as DailyData,
        byBothOptions: {} as DailyData,
        bySecondOption: {} as DailyData
    };

    // 날짜별 초기화
    dates.forEach(date => {
        results.byFirstOption[date] = {};
        results.byBothOptions[date] = {};
        results.bySecondOption[date] = {};
    });

    // 데이터 집계
    data.forEach((item) => {
        const itemDate = item.createdAt.toDate().toISOString().split('T')[0];
        
        if (dates.includes(itemDate)) {
            const firstOption = item.firstOptionTitle || '미지정';
            const secondOption = item.secondOptionTitle || '미지정';
            const combinedKey = `${firstOption} - ${secondOption}`;

            results.byFirstOption[itemDate][firstOption] = 
                (results.byFirstOption[itemDate][firstOption] || 0) + 1;
            results.byBothOptions[itemDate][combinedKey] = 
                (results.byBothOptions[itemDate][combinedKey] || 0) + 1;
            results.bySecondOption[itemDate][secondOption] = 
                (results.bySecondOption[itemDate][secondOption] || 0) + 1;
        }
    });

    // 집계 결과를 콘솔에 테이블 형태로 출력
    console.log('=== First Option 일별 집계 ===');
    console.table(results.byFirstOption);

    console.log('=== First Option - Second Option 조합 일별 집계 ===');
    console.table(results.byBothOptions);

    console.log('=== Second Option 일별 집계 ===');
    console.table(results.bySecondOption);

    setDailyResults(results);
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    // 한국 시간으로 포맷팅
    return date.toLocaleDateString('ko-KR', {
        month: 'numeric',
        day: 'numeric',
        timeZone: 'Asia/Seoul'
    }).replace('.', '/').replace('.', '');
  };

  return (
    <>
      <style>{styles}</style>
      <div className="attribution-container">
        <h1 className="page-title">어트리뷰션 분석</h1>

        <div className="table-section">
          <h2 className="table-title">First Option 일별 집계</h2>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>First Option</th>
                  {getDates().map(date => (
                    <th key={date} className="text-right">{formatDate(date)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(dailyResults.byFirstOption).length > 0 &&
                  Object.keys(Object.values(dailyResults.byFirstOption).reduce((acc, curr) => ({...acc, ...curr}), {})).map(option => (
                    <tr key={option}>
                      <td>{option}</td>
                      {getDates().map(date => (
                        <td key={date} className="text-right">
                          {(dailyResults.byFirstOption[date][option] || 0).toLocaleString()}명
                        </td>
                      ))}
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>

        <div className="table-section">
          <h2 className="table-title">First Option - Second Option 조합 일별 집계</h2>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>옵션 조합</th>
                  {getDates().map(date => (
                    <th key={date} className="text-right">{formatDate(date)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(dailyResults.byBothOptions).length > 0 &&
                  Object.keys(Object.values(dailyResults.byBothOptions).reduce((acc, curr) => ({...acc, ...curr}), {})).map(option => (
                    <tr key={option}>
                      <td>{option}</td>
                      {getDates().map(date => (
                        <td key={date} className="text-right">
                          {(dailyResults.byBothOptions[date][option] || 0).toLocaleString()}명
                        </td>
                      ))}
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>

        <div className="table-section">
          <h2 className="table-title">Second Option 일별 집계</h2>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Second Option</th>
                  {getDates().map(date => (
                    <th key={date} className="text-right">{formatDate(date)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(dailyResults.bySecondOption).length > 0 &&
                  Object.keys(Object.values(dailyResults.bySecondOption).reduce((acc, curr) => ({...acc, ...curr}), {})).map(option => (
                    <tr key={option}>
                      <td>{option}</td>
                      {getDates().map(date => (
                        <td key={date} className="text-right">
                          {(dailyResults.bySecondOption[date][option] || 0).toLocaleString()}명
                        </td>
                      ))}
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default AttributionPage;
